import _ from 'lodash';
import classnames from 'classnames';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React from 'react';

import ExpandableProfileCard from 'components/customer/profile/expandable_profile_card';
import ExternalLink from 'components/lib/external_link';
import InformationIcon from 'components/lib/icons/information_icon';
import ProfileCardDef, { ProfileCardType } from 'models/configuration/profile_card_def';
import RightArrowIcon from 'components/lib/icons/right_arrow_icon';
import { StayTransactionDef, TransactionDefType } from 'models/configuration/transaction_def';
import Tooltip from 'components/common/tooltip';
import TransactionGroup from './transaction_group';

export const StayAttributesPropTypes = PropTypes.shape({
  checkInTime: PropTypes.string,
  checkOutTime: PropTypes.string,
  name: PropTypes.string,
  url: PropTypes.string,
  title: PropTypes.string,
  address: PropTypes.string,
  neighborhood: PropTypes.string,
});

export default function StayTransactions({ isLoading, now, transactionDef, transactions, onExpand, profileCardDef }) {
  let previousStays = []; // Stays where CheckOutTime is in the past, sorted by CheckInTime desc
  let otherStays = []; // Stays with invalid CheckInTime
  // Current and upcoming stays are sorted by CheckInTime asc
  let currentStays = []; // Stays where CheckInTime < now < CheckOutTime
  let futureStays = []; // Stays where CheckInTime is in the future

  _.forEach(transactions, transaction => {
    if (!transaction.attributes) {
      return;
    }

    let checkInTime = _.get(transaction, 'attributes.checkInTime');
    let checkOutTime = _.get(transaction, 'attributes.checkOutTime');
    let timezone = _.get(transaction, 'attributes.timezone');
    let momentCheckIn = timezone ? moment(checkInTime).utcOffset(timezone) : moment(checkInTime);
    let momentCheckOut = timezone ? moment(checkOutTime).utcOffset(timezone) : moment(checkOutTime);

    if (momentCheckOut.isAfter(moment(now))) {
      if (momentCheckIn.isAfter(moment(now))) {
        futureStays.push(transaction);
      } else {
        currentStays.push(transaction);
      }
    } else {
      if (momentCheckIn.isValid()) {
        previousStays.push(transaction);
      } else {
        otherStays.push(transaction);
      }
    }
  });

  previousStays = _.orderBy(previousStays, [stay => moment(stay.attributes.checkInTime).valueOf()], ['desc']);

  const numStays = transactions.length;

  return (
    <ExpandableProfileCard
      title={_.get(profileCardDef, 'label') || `${numStays} Stay${numStays === 1 ? '' : 's'}`}
      isEmpty={!numStays}
      isLoading={isLoading}
      limit={3}
    >
      <TransactionGroup
        key="current"
        groupClassName="stayTransaction-currentStays"
        groupName="Current & Future"
        onExpand={onExpand}
        shouldExpandFirst={currentStays.length > 0}
        transactions={_.sortBy(currentStays.concat(futureStays), ['checkInTime'])}
        transactionDef={transactionDef}
        headerRenderer={attributes => (
          <ExpandedStayTransactionHeader
            now={moment().format()}
            attributes={attributes}
            transactionDef={transactionDef}
          />
        )}
        summaryRenderer={attributes => (
          <StayTransactionSummary attributes={attributes} transactionDef={transactionDef} />
        )}
      />
      {currentStays.length && (previousStays.length || otherStays.length) ? (
        <div className="stayTransaction-divider" />
      ) : null}
      <TransactionGroup
        key="previous"
        groupClassName="stayTransaction-previousStays"
        groupName="Previous"
        onExpand={onExpand}
        transactions={previousStays.concat(otherStays)}
        transactionDef={transactionDef}
        headerRenderer={attributes => (
          <ExpandedStayTransactionHeader attributes={attributes} transactionDef={transactionDef} />
        )}
        summaryRenderer={attributes => (
          <StayTransactionSummary attributes={attributes} transactionDef={transactionDef} />
        )}
      />
    </ExpandableProfileCard>
  );
}

StayTransactions.propTypes = {
  isLoading: PropTypes.bool,
  now: PropTypes.string,
  onExpand: PropTypes.func,
  profileCardDef: PropTypes.instanceOf(ProfileCardDef),
  transactionDef: PropTypes.instanceOf(StayTransactionDef),
  transactions: PropTypes.arrayOf(PropTypes.shape({ attributes: StayAttributesPropTypes })),
};

export function ExpandedStayTransactionHeader({ attributes, now, transactionDef }) {
  const { checkInTime, checkOutTime, title, address, neighborhood, timezone } = attributes;
  const headerClassNames = classnames('stayTransaction-expandedInformation-header', {
    'stayTransaction-expandedInformation-header-noOptionalAttributes': !_.some(
      _.map(transactionDef.attributes, def => attributes[def.attr])
    ),
  });

  let isCurrentStay = moment(now).isBetween(checkInTime, checkOutTime);

  let toolTipText = address || neighborhood ? _.join([address, neighborhood], '\n') : '';
  let titleText = title || '-';

  return (
    <div className={headerClassNames}>
      {isCurrentStay && <div className="stayTransaction-header-current">Current stay</div>}
      <div className="stayTransaction-header-title-wrapper">
        <div className="stayTransaction-header-title">{titleText}</div>
        {toolTipText && (
          <Tooltip className="stayTransaction-header-tooltip" message={toolTipText} position="right">
            <InformationIcon className="stayTransaction-header-tooltip-icon" />
          </Tooltip>
        )}
      </div>
      <div className="stayTransaction-header-timestamps">
        <div className="stayTransaction-header-timestamp">
          <div className="stayTransaction-header-timestamp-label">Checkin</div>
          {formatStayTimestamp(checkInTime, timezone, 'h:mm A', true)}
        </div>
        <div className="stayTransaction-header-timestamp">
          <div className="stayTransaction-header-timestamp-label">Checkout</div>
          {formatStayTimestamp(checkOutTime, timezone, 'h:mm A', true)}
        </div>
      </div>
    </div>
  );
}

ExpandedStayTransactionHeader.propTypes = {
  attributes: StayAttributesPropTypes.isRequired,
  now: PropTypes.string.isRequired,
  transactionDef: PropTypes.instanceOf(StayTransactionDef).isRequired,
};

export function StayTransactionSummary({ attributes }) {
  const { name, url, checkInTime, checkOutTime, timezone } = attributes;

  return (
    <div className="stayTransaction-summary">
      <div className="stayTransaction-name">
        <ExternalLink
          url={url}
          text={name}
          className="stayTransaction-externalLink"
          type={ProfileCardType.TRANSACTIONS}
          subType={TransactionDefType.STAY}
          attrName="url"
        />
      </div>
      <div className="stayTransaction-timestamps">
        <div className="stayTransaction-time">{formatStayTimestamp(checkInTime, timezone, 'M/D/YY')}</div>
        <div className="stayTransaction-icon">
          <RightArrowIcon className="stayTransaction-arrowIcon" />
        </div>
        <div className="stayTransaction-time">{formatStayTimestamp(checkOutTime, timezone, 'M/D/YY')}</div>
      </div>
    </div>
  );
}

StayTransactionSummary.propTypes = {
  attributes: StayAttributesPropTypes,
  transactionDef: PropTypes.instanceOf(StayTransactionDef),
};

function formatStayTimestamp(ts, tz, format, shouldIncludeTz = false) {
  if (!ts || !moment(ts).isValid()) {
    return 'N/A';
  }

  if (tz) {
    if (moment.tz.zone(tz)) {
      if (shouldIncludeTz) {
        format += ' z';
      }

      return moment(ts)
        .tz(tz)
        .format(format);
    }

    return moment(ts)
      .utcOffset(tz)
      .format(format);
  }

  return moment(ts).format(format);
}



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/customer/summary/transactions/stay_transactions.jsx